import React, { useCallback, useEffect, useState } from 'react';

import { Box, styled, Tooltip } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnHeaderParams,
} from '@mui/x-data-grid-pro';
import { isIOS } from 'react-device-detect';

import { CalcEnemy, CalcProv } from '../../../@types/Calculations';
import { Province } from '../../../@types/KingdomResponse';
import { useIntelState } from '../../../context/IntelState';
import { getWarGains } from '../../../helpers/Calculations';
import {
  letterize,
  removeDecimalsToNumber,
} from '../../../helpers/NumberFormatting';
import {
  RenderCellParams,
  ValueGetterParams,
} from '../columnDefinitions/gridColumnDefinitions';
import { simpleComparator } from '../helpers/colHelpers';

const cellCss = {
  paddingLeft: '5px',
  paddingRight: '5px',
};

const getRenderCell = (params: RenderCellParams, e: Province) => {
  const target: CalcEnemy = params.row[`#${e.slot}`];
  const thisProv: CalcProv = params.row;
  const { landGain, gainPctRounded } = getWarGains(target, thisProv);
  const styles = {
    color:
      gainPctRounded >= 11
        ? 'LightGreen'
        : gainPctRounded < 8
        ? 'OrangeRed'
        : 'inherit',
  };
  return (
    <div style={styles}>
      <span>{`${landGain}a`}</span>
      <span style={{ fontSize: '11px' }}>{` (${gainPctRounded}%)`}</span>
    </div>
  );
};

const getRow = (p: Province, enemySortedprovinces: Province[]) => {
  const sci = p.sos?.books.find((b) => b.type === 'Siege');
  const row: CalcProv = {
    id: p.id,
    slot: p.slot,
    name: p.name,
    networth: p.sot ? p.sot.networth : 0,
    siege: sci ? (100 + sci.effect) / 100 : 1,
  };
  enemySortedprovinces.forEach((e: Province) => {
    const enemy: CalcEnemy = {
      slot: e.slot,
      name: e.name,
      land: e.land,
      networth: e.sot?.networth,
      map: e.sot?.map,
      netDef: e.som?.defPointsHome,
    };
    row[`#${e.slot}`] = enemy;
  });
  return row;
};

const getCols = (enemySortedprovinces: Province[]) => {
  const staticCols: GridColDef[] = [
    { field: 'slot', headerName: '#', width: 10, disableColumnMenu: true },
    {
      field: 'name',
      headerName: 'Province',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      minWidth: 100,
      maxWidth: 140,
    },
    {
      field: 'networth',
      headerName: 'NW',
      maxWidth: 40,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params: RenderCellParams) =>
        letterize(
          params.row.sot ? params.row.sot.networth : params.row.networth
        ),
      valueGetter: (params: ValueGetterParams) =>
        params.row.sot ? params.row.sot.networth : params.row.networth,
      sortComparator: simpleComparator,
    },
  ];
  const newcols: GridColDef[] = [...staticCols];
  enemySortedprovinces.forEach((e: Province) => {
    newcols.push({
      field: `#${e.slot}`,
      width: 90,
      disableColumnMenu: true,
      sortable: false,
      headerClassName: 'e-header',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Tooltip title={`${e.name} (${e.race}/${e.sot?.personality})`} arrow>
          <div style={{ height: '35px' }}>
            <div style={{ height: '20px' }}>
              <span>{`#${e.slot}`}</span>
              {e.sot?.networth && (
                <span
                  style={{ height: '20px', color: 'Orange', fontSize: '10px' }}
                >
                  {' '}
                  {`${removeDecimalsToNumber(e.sot.networth / 1000, 0)}k NW`}
                </span>
              )}
            </div>

            {e.sot && (
              <div
                style={{
                  height: '15px',
                  fontSize: '11px',
                }}
              >
                <span style={{ color: 'LightGreen' }}>{`${e.sot?.land}a`}</span>
                <span style={{ color: 'Orange' }}>
                  {' '}
                  {`${removeDecimalsToNumber(
                    (e.som ? e.som.defPointsHome : e.sot.defPoints) / 1000,
                    0
                  )}k Def`}
                </span>
              </div>
            )}
          </div>
        </Tooltip>
      ),
      renderCell: (params: RenderCellParams) => getRenderCell(params, e),
    });
  });
  return newcols;
};

const WarGrid = () => {
  const { intelState } = useIntelState();
  const [data, setData] = useState<CalcProv[] | undefined>(undefined);
  const [cols, setCols] = useState<GridColDef[]>([]);
  const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    '& .MuiDataGrid-iconSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
      borderRight: `1px solid ${
        theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'
      }`,
    },
    '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
      borderBottom: `1px solid ${
        theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'
      }`,
    },
  }));

  const transposeData = useCallback(() => {
    const enemySortedprovinces = intelState.enemyData
      ? [...intelState.enemyData.provinces].sort((a: Province, b: Province) =>
          b.sot && a.sot ? b.sot?.networth - a.sot.networth : 0
        )
      : [];
    const newData: CalcProv[] = [];
    intelState.ownData?.provinces.forEach((p: Province) => {
      newData.push(getRow(p, enemySortedprovinces));
    });
    setData([...newData].sort((a, b) => b.networth - a.networth));
    setCols(getCols(enemySortedprovinces));
  }, [intelState.enemyData, intelState.ownData?.provinces]);

  useEffect(() => {
    if (!data) {
      transposeData();
    }
  }, [data, transposeData]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        '& .e-header': {
          lineHeight: '1rem',
        },
      }}
    >
      {data && (
        <StyledDataGrid
          autoHeight
          columns={cols}
          rows={data}
          hideFooterPagination
          hideFooter
          initialState={{
            pinnedColumns: { left: ['slot', 'name', 'networth'] },
          }}
          density='compact'
          rowHeight={40}
          sx={{
            '& .MuiDataGrid-columnHeader': cellCss,
            '& .MuiDataGrid-cell': cellCss,
          }}
        />
      )}
    </Box>
  );
};

export default WarGrid;
